import { runCreateWorkspaceWorkflow } from "@/action-dialog";
import { useCreateWorkspace } from "@/api/workspaces";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ROUTES } from "@/constants/routes";
import { useOpenSettings } from "@/context/OpenSettingsContext";
import {
  useSidebarContext,
  type WorkspaceRail,
} from "@/context/SidebarContext";
import { automationsCreateMenuItems } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/automations/AutomationsRailCreateMenu";
import { useAutomationsRailCreateActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/automations/useAutomationsRailCreateActions";
import { useChatsRailActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/chats/useChatsRailActions";
import { addSkillMenuItems } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/skills/SkillsRailAddSkillMenu";
import { useSkillsRailAddActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/skills/useSkillsRailAddActions";
import { WorkspaceRailSearch } from "@/layouts/WorkspaceLayout/components/WorkspaceRailSearch/WorkspaceRailSearch";
import { useHomeCreateActions } from "@/pages/workspaces/home/browser/useHomeCreateActions";
import { cn } from "@/utils";
import { Frame, Layers, MessagesSquare, Plus } from "lucide-react";
import { useState } from "react";

/**
 * The pane's head — the active view's create glyph + the rail search with
 * its take-over expansion (the create verb keeps the one grey; **Chat is
 * the single rust accent** — RFC 007's register). The view switchers live
 * at the app-header level (main's docked arrangement), so this row is the
 * pane's first rung.
 */

/** The action-chip register: create verbs share the ONE grey; Chat is the
 *  single accent verb. */
export const CHIP =
  "inline-flex min-h-8 items-center justify-center gap-1.5 rounded-md px-2 text-base font-medium outline-none transition-colors duration-150 ease-out hover:duration-100 focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px] [&_svg]:size-3.5 [&_svg]:shrink-0";
export const CHIP_NEUTRAL = "bg-n-05 text-n-80 hover:bg-n-10";
export const CHIP_ACCENT = "bg-rust/10 text-rust hover:bg-rust/15";

/**
 * The per-view create glyph — one square control left of the search, holding
 * the verbs that view can create. Chats is the exception the register always
 * makes: its verb IS "new chat", so the glyph is the chat mark in the single
 * rust accent and fires the verb directly instead of opening a menu.
 */
function RailCreateGlyph({ activeRail }: { activeRail: WorkspaceRail }) {
  const { onCreateChat, canCreateChat } = useChatsRailActions();
  // Canvas comes from the same shared hook as Home's "+ New" menu, so the
  // two surfaces offer the identical verb: created in your private
  // workspace (like Chat), then opened.
  const { onNewCanvas } = useHomeCreateActions();
  const createWorkspace = useCreateWorkspace();
  const { createFromScratch, openUploadZipDialog, uploadZipDialog } =
    useSkillsRailAddActions();
  const { openCreatePane, createWithAgent } = useAutomationsRailCreateActions();
  const openSettings = useOpenSettings();

  const GLYPH =
    "inline-flex size-8 shrink-0 items-center justify-center rounded-md outline-none transition-colors duration-150 ease-out hover:duration-100 focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px] [&_svg]:size-4";
  // The same quiet chip fill the icon tiles wear, so the head's create
  // control belongs to the same family as the section leads beneath it.
  const TILE_FILL = "bg-n-10 text-n-70 hover:bg-n-20 hover:text-n-90";

  if (activeRail === "chats") {
    if (!canCreateChat) return null;
    return (
      <button
        type="button"
        onClick={() => onCreateChat()}
        aria-label="New chat"
        title="New chat"
        className={cn(GLYPH, "bg-rust/10 text-rust hover:bg-rust/15")}
      >
        <MessagesSquare />
      </button>
    );
  }

  if (activeRail === "skills") {
    return (
      <>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Add skill"
              title="Add skill"
              className={cn(GLYPH, TILE_FILL)}
            >
              <Plus />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" sideOffset={4} className="w-52">
            <DropdownMenuLabel>Add</DropdownMenuLabel>
            {addSkillMenuItems(createFromScratch, openUploadZipDialog)}
          </DropdownMenuContent>
        </DropdownMenu>
        {uploadZipDialog}
      </>
    );
  }

  if (activeRail === "automations") {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="New automation"
            title="New automation"
            className={cn(GLYPH, TILE_FILL)}
          >
            <Plus />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" sideOffset={4} className="w-52">
          <DropdownMenuLabel>Create</DropdownMenuLabel>
          {automationsCreateMenuItems(openCreatePane, createWithAgent)}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (activeRail === "connectors") {
    return (
      <button
        type="button"
        onClick={() => openSettings(ROUTES.SETTINGS.CONNECTORS)}
        aria-label="Add connector"
        title="Add connector"
        className={cn(GLYPH, TILE_FILL)}
      >
        <Plus />
      </button>
    );
  }

  if (activeRail !== "workspaces") return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Create"
          title="Create"
          className={cn(GLYPH, TILE_FILL)}
        >
          <Plus />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={4} className="w-52">
        {/* The heading carries the verb, so each row is just the noun —
            "Create → Workspace" rather than a column of repeated "New"s. */}
        <DropdownMenuLabel>Create</DropdownMenuLabel>
        <DropdownMenuItem
          onSelect={() =>
            window.setTimeout(
              () => void runCreateWorkspaceWorkflow({ createWorkspace }),
              0,
            )
          }
        >
          <Layers className="size-3.5 text-n-50" />
          Workspace
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => window.setTimeout(() => onNewCanvas(), 0)}
        >
          <Frame className="size-3.5 text-n-50" />
          Canvas
        </DropdownMenuItem>
        {canCreateChat && (
          <DropdownMenuItem onSelect={() => onCreateChat()}>
            <MessagesSquare className="size-3.5 text-n-50" />
            Chat
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function RailHead() {
  const { activeRail } = useSidebarContext();
  const [searchActive, setSearchActive] = useState(false);

  return (
    <div className="shrink-0 px-2 pt-2">
      {/* The search row: one create glyph, then the field. The verb pair
          (New · Chat) collapsed into this glyph — each view's creates hang
          from it, so the head carries one control instead of two chips.

          Take-over: while the search is focused or holds text, the glyph
          folds away (width → 0) and the field takes the whole row. A live
          filter should own the row it filters. */}
      <div className="flex items-center">
        <div
          className={cn(
            "overflow-hidden transition-[width,margin,opacity] duration-150 ease-out",
            searchActive
              ? "pointer-events-none w-0 opacity-0"
              : "mr-1.5 w-8 opacity-100",
          )}
        >
          <RailCreateGlyph activeRail={activeRail} />
        </div>
        <div className="min-w-0 flex-1">
          <WorkspaceRailSearch onActiveChange={setSearchActive} />
        </div>
      </div>
    </div>
  );
}
